package dev.kuml.portal.web.auth

import dev.kuml.portal.web.i18n.strings.AuthStrings

/**
 * A resolved `?oauth=<code>` status line -- [message] the translated text, [isError] whether the
 * `AuthScreen`/profile screen should render it in the error style. Kept as a pure `data class`
 * (no DOM dependency) so [oauthNoticeFor] is trivially unit-testable in isolation.
 */
public data class OAuthNotice(
    val message: String,
    val isError: Boolean,
)

/**
 * Maps the `?oauth=<code>` query parameter the OAuth callback redirect carries (see
 * `dev.kuml.portal.server.oauth.oauthRoutes`'s KDoc for the full contract; the sibling
 * `&provider=<id>` parameter is not needed to pick a message -- every current notice's wording is
 * provider-agnostic) to a translated [OAuthNotice] -- `null` if [code] is itself `null` (no
 * OAuth-related redirect happened; the caller shows nothing). An UNRECOGNISED, non-null code still
 * gets a notice (the generic error copy) rather than silently showing nothing -- a future
 * server-side code this client does not yet know about should never disappear without a trace.
 */
public fun oauthNoticeFor(
    strings: AuthStrings,
    code: String?,
): OAuthNotice? =
    when (code) {
        null -> null
        "linked" -> null // handled by the profile screen's own success banner, not this notice
        "conflict" -> OAuthNotice(strings.oauthConflictNotice, isError = true)
        "no_email" -> OAuthNotice(strings.oauthNoEmailNotice, isError = true)
        "cancelled" -> OAuthNotice(strings.oauthCancelledNotice, isError = false)
        "unavailable" -> OAuthNotice(strings.oauthUnavailableNotice, isError = true)
        "link_session_lost" -> OAuthNotice(strings.oauthLinkSessionLost, isError = true)
        else -> OAuthNotice(strings.oauthGenericError, isError = true)
    }
